<script setup>
import { ref, onMounted, watch } from 'vue'
import apiClient from '@/api'
import Modal from 'bootstrap/js/dist/modal'
import AppLoader from '@/components/AppLoader.vue'
import Vue3EasyDataTable from 'vue3-easy-data-table'
import 'vue3-easy-data-table/dist/style.css'
import { webAlert, showConfirm } from '@/utils/dialog'

// --- 1. Konfiguracja Easy Data Table ---
const headers = [
    { text: 'ID', value: 'ID', sortable: true, width: 70 },
    { text: 'Nazwa', value: 'name', sortable: true },
    { text: 'Adres e-mail', value: 'email', sortable: true },
    { text: 'Administrator', value: 'isAdmin', sortable: true },
    { text: 'Domena', value: 'domain', sortable: true },
    { text: 'Pakiet', value: 'packet', sortable: true },
    { text: 'Szablon', value: 'template', sortable: true },
    { text: 'Ważne do', value: 'activeTo', sortable: true },
    { text: 'Własna strona', value: 'showOwnPage', sortable: true },
    { text: '', value: 'actions', width: 100 }
]

const items = ref([])
const packetsOptions = ref([])
const templatesOptions = ref([])

const loading = ref(false)
const saving = ref(false)
const savingTXT = ref('')
const searchValue = ref('')
const showPassword = ref(false)

const serverItemsLength = ref(0)
const serverOptions = ref({
    page: 1,
    rowsPerPage: 10,
    sortBy: 'ID',
    sortType: 'desc'
})

// --- Pobieranie słowników dla Selectów ---
const loadDictionaries = async () => {
    try {
        const resPackets = await apiClient.post(
            { action: 'get-packets' },
        )
        packetsOptions.value = Array.isArray(resPackets.data?.data) ? resPackets.data.data : (Array.isArray(resPackets.data) ? resPackets.data : [])

        const resTemplates = await apiClient.post(
            { action: 'get-templates' },
        )
        templatesOptions.value = Array.isArray(resTemplates.data?.data) ? resTemplates.data.data : (Array.isArray(resTemplates.data) ? resTemplates.data : [])
    } catch (error) {
        console.error('Błąd podczas pobierania słowników:', error)
    }
}

// --- 2. Pobieranie danych z API ---
const loadData = async () => {
    loading.value = true
    try {
        const response = await apiClient.post(
            {
                method: 'post',
                action: 'get-dealers',
                page: serverOptions.value.page,
                limit: serverOptions.value.rowsPerPage,
                sortBy: serverOptions.value.sortBy,
                sortType: serverOptions.value.sortType,
                search: searchValue.value
            }
        )

        items.value = Array.isArray(response.data?.data) ? response.data.data : []
        serverItemsLength.value = response.data?.total || 0
    } catch (error) {
        webAlert('Błąd podczas ładowania danych z serwera.')
    } finally {
        loading.value = false
    }
}

watch(
    serverOptions,
    () => { loadData() },
    { deep: true }
)

let searchTimeout = null
const handleSearch = () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        serverOptions.value.page = 1
        loadData()
    }, 400)
}

// --- 3. Obsługa Modala i Formularza ---
const modalRef = ref(null)
const formRef = ref(null)
let modalInstance = null

const formTitle = ref('Dodaj dealera')
const formData = ref({
    ID: '',
    name: '',
    email: '',
    password: '',
    isAdmin: false,
    domain: '',
    packetID: '',
    templateID: '',
    activeTo: '',
    showOwnPage: false
})

const openAddModal = () => {
    formTitle.value = 'Dodaj nowego dealera'
    showPassword.value = false
    formData.value = {
        ID: '',
        name: '',
        email: '',
        password: '',
        isAdmin: false,
        domain: '',
        packetID: '',
        templateID: '',
        activeTo: '',
        showOwnPage: false
    }
    modalInstance.show()
}

const openEditModal = async (id) => {
    savingTXT.value = 'Pobieranie danych ...'
    saving.value = true
    showPassword.value = false
    try {
        const response = await apiClient.post({
                action: 'get-dealers',
                ID: id
        })


        const result = await response.data
        const item = result.data ? result.data[0] : result

        formTitle.value = `Edytuj: ${item.name || item.email}`

        // Formatowanie daty dla datetime-local (YYYY-MM-DDTHH:mm)
        let formattedDate = ''
        if (item.activeTo) {
            formattedDate = String(item.activeTo).replace(' ', 'T').substring(0, 16)
        }

        formData.value = {
            ...item,
            ID: item.ID || item.id,
            password: '',
            isAdmin: item.isAdmin === 'yes' || item.isAdmin === 1 || item.isAdmin === true,
            showOwnPage: item.showOwnPage === 'yes' || item.showOwnPage === 1 || item.showOwnPage === true,
            // Konwersja wartości na String dla dokładnego dopasowania w <select>
            packetID: item.packetID !== null && item.packetID !== undefined ? String(item.packetID) : '',
            templateID: item.templateID !== null && item.templateID !== undefined ? String(item.templateID) : '',
            activeTo: formattedDate
        }

        modalInstance.show()
    } catch (error) {
        webAlert('Błąd podczas pobierania danych elementu.')
    } finally {
        saving.value = false
    }
}

const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()'
    let pass = ''
    for (let i = 0; i < 12; i++) {
        pass += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    formData.value.password = pass
    showPassword.value = true // Odkrywamy pole, aby wygenerowane hasło było od razu widoczne
}

const handleSubmit = async () => {
    savingTXT.value = 'Zapisywanie ...'
    saving.value = true
    try {
        // Jeśli używamy datetime-local, zamieniamy 'T' na spację do zapisu w bazie (YYYY-MM-DD HH:mm:ss)
        let activeToPayload = formData.value.activeTo ? formData.value.activeTo.replace('T', ' ') : null
        if (activeToPayload && activeToPayload.length === 16) {
            activeToPayload += ':00' // Dodanie sekund, jeśli baza wymaga pełnego formatu HH:mm:ss
        }

        const payload = {
            action: 'save-dealer',
            id: formData.value.ID || null,
            name: formData.value.name,
            email: formData.value.email,
            password: formData.value.password,
            domain: formData.value.domain,
            packetID: formData.value.packetID || null,
            templateID: formData.value.templateID || null,
            activeTo: activeToPayload,
            showOwnPage: formData.value.showOwnPage ? 'yes' : 'no',
            isAdmin: formData.value.isAdmin ? 'yes' : 'no'
        }

        if (formData.value.password) {
            payload.password = formData.value.password
        }

        const response = await apiClient.post(payload)

        modalInstance.hide()
        saving.value = false
        await loadData()

        webAlert(response.data);
    } catch (error) {
        saving.value = false
        webAlert('Błąd podczas zapisywania danych.')
    }
}

const deleteItem = async (id) => {
    const confirmed = await showConfirm('Czy na pewno chcesz usunąć tego dealera?', {
        title: 'Usuwanie pozycji',
        confirmText: 'Usuń',
        cancelText: 'Anuluj',
        confirmClass: 'btn-danger',
        icon: 'bi-trash text-danger'
    })

    if (!confirmed) return

    saving.value = true
    savingTXT.value = "Kasowanie ..."
    try {
        const response = await apiClient.post(
            {
                action: 'delete-dealer',
                id: id
            }
        )

        await loadData()

        webAlert(response.data)
    } catch (error) {
        webAlert('Błąd podczas usuwania pozycji.')
    } finally {
        saving.value = false
    }
}

onMounted(() => {
    modalInstance = new Modal(modalRef.value)
    modalRef.value.addEventListener('shown.bs.modal', () => {
        const firstInput = modalRef.value.querySelector('input:not([type="hidden"]), select, textarea')
        if (firstInput) {
            firstInput.focus()
        }
    })

    loadDictionaries()
    loadData()
})
</script>

<template>
    <div class="container-fluid">
        <div
            class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 px-3 px-md-0">
            <div>
                <h3 class="fw-bold mb-1">Dealerzy</h3>
                <p class="text-muted small mb-0">Zarządzanie dealerami i kontami w systemie</p>
            </div>
            <button
                class="btn btn-primary-dark d-flex align-items-center justify-content-center gap-2 w-100 btn-responsive-auto"
                @click="openAddModal">
                <i class="bi bi-plus-lg"></i>
                <span>Dodaj nowy</span>
            </button>
        </div>

        <div class="card border-0 shadow-sm">
            <div class="card-body">
                <div class="row">
                    <div class="col-12 col-md-8"></div>
                    <div class="col-12 col-md-4">
                        <input v-model="searchValue" @input="handleSearch" type="text" class="form-control mb-3"
                            placeholder="Szukaj..." />
                    </div>
                </div>

                <div class="table-responsive">
                    <Vue3EasyDataTable v-model:server-options="serverOptions" :headers="headers" :items="items"
                        :loading="loading" :server-items-length="serverItemsLength" buttons-pagination
                        class="table-font-13" table-class-name="table table-hover align-middle custom-easy-table"
                        :rows-items="[10, 25, 50, 100]" empty-message="Brak danych do wyświetlenia"
                        rows-per-page-message="Wierszy na stronę:">

                        <template #loading>
                            <div class="py-4 text-center">Pobieranie danych, proszę czekać...</div>
                        </template>

                        <template #item-isAdmin="item">
                            <span v-if="item.isAdmin === 'yes' || item.isAdmin === true || item.isAdmin === 1"
                                class="badge bg-success-subtle text-success border border-success-subtle">Tak</span>
                            <span v-else class="badge bg-light text-muted border">Nie</span>
                        </template>

                        <template #item-showOwnPage="item">
                            <span
                                v-if="item.showOwnPage === 'yes' || item.showOwnPage === true || item.showOwnPage === 1"
                                class="badge bg-primary-subtle text-primary border border-primary-subtle">Tak</span>
                            <span v-else class="badge bg-light text-muted border">Nie</span>
                        </template>

                        <template #item-actions="item">
                            <div class="d-flex justify-content-end gap-1">
                                <button class="btn btn-sm btn-primary-light" title="Edytuj"
                                    @click="openEditModal(item.ID || item.id)">
                                    <i class="bi bi-pencil"></i>
                                </button>
                                <button class="btn btn-sm btn-outline-danger" title="Usuń"
                                    @click="deleteItem(item.ID || item.id)">
                                    <i class="bi bi-trash"></i>
                                </button>
                            </div>
                        </template>
                    </Vue3EasyDataTable>
                </div>
            </div>
        </div>

        <!-- Modal Formularza -->
        <div ref="modalRef" class="modal fade" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title fw-bold">{{ formTitle }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <form ref="formRef" @submit.prevent="handleSubmit">
                        <div class="modal-body">
                            <input type="hidden" name="id" :value="formData.ID" />

                            <div class="mb-3">
                                <label class="form-label">Nazwa</label>
                                <input type="text" class="form-control" name="name" v-model="formData.name" />
                            </div>

                            <div class="mb-3">
                                <label class="form-label">Email</label>
                                <input type="email" class="form-control" name="email" v-model="formData.email"
                                    required />
                            </div>

                            <div class="mb-3">
                                <label class="form-label">Hasło <span v-if="formData.ID"
                                        class="text-muted small">(pozostaw
                                        puste, aby nie zmieniać)</span></label>
                                <div class="input-group">
                                    <input id="passwordInput" v-model="formData.password"
                                        :type="showPassword ? 'text' : 'password'" class="form-control" placeholder=""
                                        :required="!formData.ID" :disabled="saving" />
                                    <button class="btn btn-outline-secondary" type="button"
                                        @click="showPassword = !showPassword" :disabled="saving">
                                        <i class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
                                    </button>
                                    <button class="btn btn-outline-secondary" type="button" title="Generuj losowe hasło"
                                        @click="generatePassword" :disabled="saving">
                                        <i class="bi bi-shuffle"></i>
                                    </button>
                                </div>
                            </div>

                            <div class="mb-3">
                                <label class="form-label">Domena</label>
                                <input type="text" class="form-control" name="domain" v-model="formData.domain" />
                            </div>

                            <div class="mb-3">
                                <label class="form-label">Pakiet</label>
                                <select class="form-select" name="packetID" v-model="formData.packetID">
                                    <option value="">-- Brak / Wybierz pakiet --</option>
                                    <option v-for="option in packetsOptions"
                                        :key="option.id || option.ID || option.value"
                                        :value="String(option.value || option.id || option.ID)">
                                        {{ option.name || option.label || option.value }}
                                    </option>
                                </select>
                            </div>

                            <div class="mb-3">
                                <label class="form-label">Szablon (Template)</label>
                                <select class="form-select" name="templateID" v-model="formData.templateID">
                                    <option value="">-- Brak / Wybierz szablon --</option>
                                    <option v-for="option in templatesOptions"
                                        :key="option.id || option.ID || option.value"
                                        :value="String(option.value || option.id || option.ID)">
                                        {{ option.name || option.label || option.value }}
                                    </option>
                                </select>
                            </div>

                            <div class="mb-3">
                                <label class="form-label">Aktywny do</label>
                                <input type="datetime-local" class="form-control" name="activeTo"
                                    v-model="formData.activeTo" />
                            </div>

                            <div class="form-check mb-2">
                                <input class="form-check-input" type="checkbox" id="showOwnPageCheck"
                                    v-model="formData.showOwnPage">
                                <label class="form-check-label" for="showOwnPageCheck">
                                    Ma własną stronę www
                                </label>
                            </div>

                            <div class="form-check mb-3">
                                <input class="form-check-input" type="checkbox" id="isAdminCheck"
                                    v-model="formData.isAdmin">
                                <label class="form-check-label" for="isAdminCheck">
                                    Jest administratorem systemu
                                </label>
                            </div>
                        </div>

                        <div class="modal-footer">
                            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Anuluj</button>
                            <button type="submit" class="btn btn-primary">Zapisz zmiany</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
    <div id="glownyloader">
        <AppLoader :show="saving" :text="savingTXT" />
    </div>
</template>

<style scoped>
.table-responsive {
    display: block;
    width: 100%;
    overflow-x: auto;
    max-width: calc(100vw - 2rem) !important;
    -webkit-overflow-scrolling: touch;
}

:deep(.vue3-easy-data-table) {
    --easy-table-border: 1px solid #dee2e6;
    --easy-table-row-border: 1px solid #dee2e6;
    --easy-table-header-font-size: 0.85rem;
    --easy-table-header-height: 45px;
    --easy-table-body-row-height: 50px;
    width: 100% !important;
    max-width: 100% !important;
}

:deep(.vue3-easy-data-table__main) {
    width: 100% !important;
    overflow-x: auto !important;
}

.buttons-pagination .item.button.active {
    background-color: #043873 !important;
}
</style>